/* eslint-disable array-callback-return */
import { getObjectValues } from '../../../utils/GlobalUtils';
import { getOptionAvailability, getProduct, OPTION_TYPE } from './Product';

const variantsDefaultImagesCache = {};

export const getOptionId = ({ id, optionId } = {}) => id || optionId;

export const setCachedVariantsDefaultImages = ({ parentProductId, variantsDefaultImages }) => {
  variantsDefaultImagesCache[parentProductId] = variantsDefaultImages;
};

export const getCachedVariantsDefaultImages = ({ parentProductId }) =>
  variantsDefaultImagesCache[parentProductId];

const fetchTopNOptions = ({ topN, options }) => {
  if (!Array.isArray(options)) {
    return null;
  }

  if (!topN) {
    return options;
  }

  return options.slice(0, topN);
};

export const getAllOptionsBasedOnStockSettings = (
  { variantId, optionType, fromCustomPage },
  { topN } = {}
) => {
  const product = getProduct(variantId, fromCustomPage) || {};

  const { parentProductId, isVariant } = product;

  const parentProduct = getProduct(parentProductId, fromCustomPage);
  if (
    !parentProductId ||
    !optionType ||
    typeof isVariant !== 'function' ||
    !isVariant() ||
    !parentProduct
  ) {
    return null;
  }

  const { availableVariants, unavailableVariants, allOptions } = parentProduct;
  const requiredOptions = (allOptions || {})[optionType];
  if (!Array.isArray(requiredOptions)) {
    return null;
  }

  let unavailableOptionIdMap = (unavailableVariants || []).reduce((cummulativeMap, id) => {
    const { optionsMeta } = getProduct(id) || {};
    if (!optionsMeta) {
      return;
    }
    const { optionId } = optionsMeta[optionType] || {};
    cummulativeMap[optionId] = true;

    return cummulativeMap;
  }, {});

  if (!unavailableOptionIdMap) {
    unavailableOptionIdMap = {};
  }

  (availableVariants || []).forEach((id) => {
    const { optionsMeta } = getProduct(id) || {};
    if (!optionsMeta) {
      return;
    }
    const { optionId } = optionsMeta[optionType] || {};
    if (optionId) {
      delete unavailableOptionIdMap[optionId];
    }
  });

  const filteredOptions = requiredOptions.filter(
    ({ optionId }) => !unavailableOptionIdMap[optionId]
  );

  return fetchTopNOptions({ topN, options: filteredOptions });
};

export const filterAvailableOptionsBasedOnStockSettings = (
  { variantId, optionType },
  extraOptions
) => {
  const options = getAllOptionsBasedOnStockSettings({ variantId, optionType }, extraOptions);
  if (!Array.isArray(options)) {
    return options;
  }

  // const unavailableOptions = getOptionAvailability(
  //   { productId: variantId, optionType },
  //   { checkOOSConfig: true }
  // );
  return options; // .filter(({ optionId }) => !unavailableOptions[optionId]);
};

export const getColorOptionAvailability = ({ variantId }) => {
  const optionType = OPTION_TYPE.COLORS.id;
  const unavailableOptions = getOptionAvailability({ productId: variantId, optionType });

  const product = getProduct(variantId);
  const { parentProductId } = product || {};
  const parentProduct = getProduct(parentProductId);
  if (!parentProduct) {
    return unavailableOptions;
  }

  // Can later be scaled to fetch all options apart from the color
  const sizeOptions = filterAvailableOptionsBasedOnStockSettings({
    variantId,
    optionType: OPTION_TYPE.SIZES.id
  });

  if (!Array.isArray(sizeOptions)) {
    return unavailableOptions;
  }

  const availableSizeValues = sizeOptions.map(({ optionValue }) => optionValue);

  const { availableVariants } = parentProduct;

  // Go through all unavailable options and verify which combinations exist
  const modifiedUnavailableOptions = Object.keys(unavailableOptions).reduce(
    (cumulativeMap, optionId) => {
      const optionData = { ...unavailableOptions[optionId] };
      const availableMap = {};

      // Go through the available variants to get the valid combinations with the
      // current unavailable option. This will provide the sizes that are currently
      // mapped and valid with the current option
      availableVariants.forEach((availableVariantId) => {
        const { optionsMeta } = getProduct(availableVariantId) || {};
        const { optionId: currentOptionId } = optionsMeta[optionType] || {};
        const variantHasCurrentOptionId = currentOptionId === optionId;
        if (!variantHasCurrentOptionId) {
          return;
        }

        // Go through the current variant's options meta and put the other variant combination
        // values in a map of available variants. Later on can be scaled to be a map of option type
        // which is a map of option values
        getObjectValues(optionsMeta).forEach(({ optionValue, optionType: metaOptionType }) => {
          if (metaOptionType === optionType) {
            return;
          }

          availableMap[optionValue] = true;
        });
      });

      optionData.unavailableList = availableSizeValues.reduce((cumulativeArray, sizeValue) => {
        if (!availableMap[sizeValue]) {
          cumulativeArray.push(sizeValue);
        }
        return cumulativeArray;
      }, []);

      optionData.availableList = Object.keys(availableMap);

      cumulativeMap[optionId] = optionData;
      return cumulativeMap;
    },
    {}
  );

  return modifiedUnavailableOptions;
};
